import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles } from "lucide-react";
import TimeSelector, { TimeValue } from "./TimeSelector";
import { getSystemPromptPreview } from "../../../utils/rentalsSystemPrompt";
import type { ToolType } from "../../../types";

export type AgentFormData = {
  agentName: string;
  politicaTratamientoDeDatos: string;
  greetingsMessage: string;
  coveredAreas: string;
  webPage: string;
  rentalsRequirements: string;
  weekDayStart: TimeValue;
  weekDayEnd: TimeValue;
  visitOnSaturdayAllowed: boolean;
  saturdayDayStart: TimeValue;
  saturdayDayEnd: TimeValue;
  visitOnSundayAllowed: boolean;
  sundayDayStart: TimeValue;
  sundayDayEnd: TimeValue;
  minimumAdvanceHoursSales: number | undefined;
  minimumAdvanceHoursRent: number | undefined;
  selectedTools: ToolType[];
};

type Step2AgentConfigurationProps = {
  agentFormData: AgentFormData;
  onFormDataChange: (data: AgentFormData) => void;
  displayName: string;
  enableContactEmployeeButton?: boolean;
  calComIntegrationEnabled?: boolean;
};

const Step2AgentConfiguration = ({
  agentFormData,
  onFormDataChange,
  displayName,
  enableContactEmployeeButton,
  calComIntegrationEnabled,
}: Step2AgentConfigurationProps) => {
  const getDisplayGreetingMessage = () => {
    return agentFormData.greetingsMessage
      .replace(/{nombreAgente}/g, agentFormData.agentName || "{nombreAgente}")
      .replace(
        /{politicaTratamientoDeDatos}/g,
        agentFormData.politicaTratamientoDeDatos || "{politicaTratamientoDeDatos}"
      );
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-semibold mb-4">
        Configuracion del Agente Comercial
      </p>
      {/* Split layout with form on left and prompt preview on right */}
      <div className="flex gap-6 items-start">
        {/* Left side - Form */}
        <div className="flex-1">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Nombre del agente <span className="text-red-500">*</span>
              </Label>
              <Input
                value={agentFormData.agentName}
                onChange={(e) => onFormDataChange({ ...agentFormData, agentName: e.target.value })}
                placeholder="Ingresa el nombre del agente"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Enlace a la politica de tratamiento de datos <span className="text-red-500">*</span>
              </Label>
              <Input
                value={agentFormData.politicaTratamientoDeDatos}
                onChange={(e) =>
                  onFormDataChange({
                    ...agentFormData,
                    politicaTratamientoDeDatos: e.target.value,
                  })
                }
                placeholder="https://ejemplo.com/politica-de-datos"
                type="url"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Saludo (incluye la politica de tratamiento de datos)
              </Label>
              <Textarea
                value={getDisplayGreetingMessage()}
                onChange={(e) =>
                  onFormDataChange({
                    ...agentFormData,
                    greetingsMessage: e.target.value,
                  })
                }
                placeholder="Mensaje de saludo del agente"
                rows={4}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Areas de cobertura</Label>
              <Textarea
                value={agentFormData.coveredAreas}
                onChange={(e) =>
                  onFormDataChange({ ...agentFormData, coveredAreas: e.target.value })
                }
                placeholder="Areas de cobertura de alto nivel"
                rows={2}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Pagina web de la inmobiliaria <span className="text-red-500">*</span>
              </Label>
              <Input
                value={agentFormData.webPage}
                onChange={(e) => onFormDataChange({ ...agentFormData, webPage: e.target.value })}
                placeholder="https://ejemplo.com"
                type="url"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Requisitos de arrendamiento</Label>
              <Textarea
                value={agentFormData.rentalsRequirements}
                onChange={(e) =>
                  onFormDataChange({
                    ...agentFormData,
                    rentalsRequirements: e.target.value,
                  })
                }
                placeholder="Requisitos de arrendamiento"
                rows={6}
              />
            </div>

            {/* Time Range Configuration */}
            <div>
              <p className="text-base font-semibold mb-4">
                Rangos de horario para visitas
              </p>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-sm font-medium mb-2">
                    Lunes a Viernes:
                  </p>
                  <div className="flex items-center gap-4">
                    <TimeSelector
                      timeValue={agentFormData.weekDayStart}
                      onChange={(value) =>
                        onFormDataChange({
                          ...agentFormData,
                          weekDayStart: value,
                        })
                      }
                      label="Desde"
                    />
                    <TimeSelector
                      timeValue={agentFormData.weekDayEnd}
                      onChange={(value) =>
                        onFormDataChange({
                          ...agentFormData,
                          weekDayEnd: value,
                        })
                      }
                      label="Hasta"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      checked={agentFormData.visitOnSaturdayAllowed}
                      onCheckedChange={(checked) =>
                        onFormDataChange({
                          ...agentFormData,
                          visitOnSaturdayAllowed: checked === true,
                        })
                      }
                    />
                    <label className="text-sm font-medium">
                      Permitir visitas los sabados
                    </label>
                  </div>
                  {agentFormData.visitOnSaturdayAllowed && (
                    <div className="flex items-center gap-4">
                      <TimeSelector
                        timeValue={agentFormData.saturdayDayStart}
                        onChange={(value) =>
                          onFormDataChange({
                            ...agentFormData,
                            saturdayDayStart: value,
                          })
                        }
                        label="Desde"
                      />
                      <TimeSelector
                        timeValue={agentFormData.saturdayDayEnd}
                        onChange={(value) =>
                          onFormDataChange({
                            ...agentFormData,
                            saturdayDayEnd: value,
                          })
                        }
                        label="Hasta"
                      />
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      checked={agentFormData.visitOnSundayAllowed}
                      onCheckedChange={(checked) =>
                        onFormDataChange({
                          ...agentFormData,
                          visitOnSundayAllowed: checked === true,
                        })
                      }
                    />
                    <label className="text-sm font-medium">
                      Permitir visitas los domingos
                    </label>
                  </div>
                  {agentFormData.visitOnSundayAllowed && (
                    <div className="flex items-center gap-4">
                      <TimeSelector
                        timeValue={agentFormData.sundayDayStart}
                        onChange={(value) =>
                          onFormDataChange({
                            ...agentFormData,
                            sundayDayStart: value,
                          })
                        }
                        label="Desde"
                      />
                      <TimeSelector
                        timeValue={agentFormData.sundayDayEnd}
                        onChange={(value) =>
                          onFormDataChange({
                            ...agentFormData,
                            sundayDayEnd: value,
                          })
                        }
                        label="Hasta"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Minimum Advance Hours Configuration */}
            <div>
              <p className="text-base font-semibold mb-4">
                Tiempo minimo de antelacion para solicitar una cita
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm">
                    Horas minimas habiles de antelacion para ventas
                  </Label>
                  <input
                    type="number"
                    min={0}
                    value={agentFormData.minimumAdvanceHoursSales ?? ""}
                    onChange={(e) => {
                      const value = e.target.value === "" ? undefined : parseInt(e.target.value);
                      onFormDataChange({
                        ...agentFormData,
                        minimumAdvanceHoursSales: value,
                      });
                    }}
                    placeholder="Ej: 8"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm">
                    Horas minimas habiles de antelacion para arriendos
                  </Label>
                  <input
                    type="number"
                    min={0}
                    value={agentFormData.minimumAdvanceHoursRent ?? ""}
                    onChange={(e) => {
                      const value = e.target.value === "" ? undefined : parseInt(e.target.value);
                      onFormDataChange({
                        ...agentFormData,
                        minimumAdvanceHoursRent: value,
                      });
                    }}
                    placeholder="Ej: 24"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Prompt Preview and Tools */}
        <div className="flex-1">
          <div className="flex flex-col gap-6">
            {/* Prompt Preview */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Sparkles className="size-5 text-purple-500" />
                <p className="text-base font-semibold">
                  Vista previa de las instrucciones
                </p>
              </div>
              <Textarea
                value={getSystemPromptPreview({
                  agentFormData,
                  displayName,
                  greetingsMessage: getDisplayGreetingMessage(),
                  enableContactEmployeeButton,
                  calComIntegrationEnabled,
                })}
                readOnly
                className="h-[121vh] resize-y bg-gray-50 border border-gray-200 text-sm font-mono p-4 overflow-y-auto"
              />
            </div>

            {/* Tools Selection */}
            <div>
              <p className="text-base font-semibold mb-4">
                Herramientas
              </p>
              <div className="flex flex-col gap-2">
                {[
                  { value: "transferConversation", label: "Transferir Conversacion" },
                  { value: "scheduleVisit", label: "Agendar Visita" },
                  { value: "getAvailableSlots", label: "Obtener horarios disponibles" },
                  { value: "getProperty", label: "Obtener Propiedad" },
                  { value: "getPropertyByCode", label: "Obtener Propiedad por Codigo" },
                  { value: "searchProperties", label: "Buscar Propiedades" },
                  { value: "assignAgent", label: "Asignar Agente" },
                  { value: "setEmail", label: "Correo electrónico" },
                  { value: "registerListingRequest", label: "Registrar Solicitud de Publicacion" },
                  { value: "discardLead", label: "Descartar Lead" },
                  { value: "calculateDaysFor", label: "Calcular Dias" },
                  {
                    value: "createMaintenanceRequest",
                    label: "Registrar Solicitud de Mantenimiento",
                  },
                  { value: "getCustomerProperties", label: "Obtener inmuebles del cliente" },
                  { value: "getCustomerDocuments", label: "Obtener documentos del cliente" },
                  { value: "createServiceRequest", label: "Crear solicitud de servicio" },
                  { value: "updateServiceRequest", label: "Actualizar solicitud de servicio" },
                  {
                    value: "updatePipelinePosition",
                    label: "Actualizar estado del usuario",
                  },
                  {
                    value: "requestRentGuaranteePreApproval",
                    label: "Solicitar estudio de fianza",
                  },
                  {
                    value: "lookupByContractNumber",
                    label: "Buscar por Numero de Contrato",
                  },
                  {
                    value: "setAppointmentIntention",
                    label: "Registrar intencion de cita",
                  },
                  { value: "sendMedia", label: "Enviar multimedia" },
                ].map((tool) => (
                  <div key={tool.value} className="flex items-center gap-2">
                    <Checkbox
                      checked={
                        agentFormData.selectedTools?.includes(tool.value as ToolType) ?? false
                      }
                      onCheckedChange={(checked) => {
                        const currentTools = agentFormData.selectedTools || [];
                        const toolValue = tool.value as ToolType;
                        const newTools = checked
                          ? [...currentTools, toolValue]
                          : currentTools.filter((t) => t !== toolValue);
                        onFormDataChange({
                          ...agentFormData,
                          selectedTools: newTools,
                        });
                      }}
                    />
                    <label className="text-sm">{tool.label}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2AgentConfiguration;
